import { GameScene } from "../../../shared/scenes/Game";
import { ServerTank } from "../entities/ServerTank";
import { InputData } from "../../../shared/objects/Tank";
import type { SnapshotInterpolation } from "@geckos.io/snapshot-interpolation";
import type { MyRoomState, NewswireMessage } from "../schemas/RoomState";
import { ServerMap } from "../scenes/ServerMap";
import { StationSchema } from "../schemas/StationSchema";
import { PillboxSchema } from "../schemas/PillboxSchema";
import { TEAM_COLORS, TILE_INDICES, PHYSICS } from "../../../shared/constants";
import { ServerPillbox } from "../entities/ServerPillbox";
import { ServerBullet } from "../entities/ServerBullet";
import { ServerStation } from "../entities/ServerStation";

export interface WorldState {
  players: ServerTank[];
  bullets: ServerBullet[];
  pillboxes: ServerPillbox[];
  stations: ServerStation[];
}

// The subset of the Colyseus room the game scene depends on. Both the real
// GameRoom (online) and the browser LocalRoom (offline) implement this, so the
// authoritative simulation runs unchanged in either environment.
export interface GameRoomLike {
  state: MyRoomState;
  SI: SnapshotInterpolation;
  broadcast(type: string, message?: any): void;
  broadcastNewswire(message: NewswireMessage): void;
  sendToClient(sessionId: string, type: string, message?: any): void;
}

export class ServerGameScene extends GameScene {
  // Map of players by session ID
  players: Map<string, ServerTank> = new Map();
  room: GameRoomLike;

  worldState: WorldState = {
    players: [],
    bullets: [],
    pillboxes: [],
    stations: [],
  };
  
  // Track server-managed bullets
  serverBullets: ServerBullet[] = [];

  constructor(config: Phaser.Types.Scenes.SettingsConfig) {
    super(config);
    console.log("ServerGameScene constructor");
  }

  // Store the selected map name
  selectedMapName: string = "Duff Gardens";
  
  preload() {
    // The map name is chosen by the caller (GameRoom picks a random one via mapList).
    console.log(`Loading map: ${this.selectedMapName}`);
    this.load.image('tileset', '../../../../assets/tiles/tileset'+PHYSICS.TILE_SIZE+'.png');
    this.load.json('mapData', `../../../../assets/maps/${this.selectedMapName}.json`);
    this.load.json('tilesetData', '../../../../assets/tiles/tileset'+PHYSICS.TILE_SIZE+'.json');
  }

  create() {
    super.create();
    // Create the game map
    this.gameMap = new ServerMap(this);
    this.gameMap.createTilemapFromFile();
    this.room.state.map = this.gameMap.schema;
    
    // Broadcast the map name to all players
    setTimeout(() => {
      this.sendNewswire({
        type: 'info',
        message: `Map: ${this.selectedMapName}`,
      });
      console.log(`Game started with map: ${this.selectedMapName}`);
    }, 2000); // Small delay to ensure clients are ready

    if (!this.gameMap.map) {
        console.error("Failed to create tilemap");
        return;
    }        
    console.log(`Tilemap created successfully: ${this.gameMap.map.width}x${this.gameMap.map.height} tiles, ${this.gameMap.map.widthInPixels}x${this.gameMap.map.heightInPixels} pixels`);
    
    // Set world bounds to match the tilemap size
    const mapWidth = this.gameMap.map.widthInPixels;
    const mapHeight = this.gameMap.map.heightInPixels;
    this.matter.world.setBounds(0, 0, mapWidth, mapHeight);
    
    // Add stations to the map
    const mapData = this.cache.json.get('mapData');
    if (mapData.stations) {
        const stationLocations = mapData.stations;
        
        for (let i = 0; i < stationLocations.length; i++) {
            const location = stationLocations[i];
            const worldLocation = this.gameMap.groundLayer.tileToWorldXY(location[0]*2+1, location[1]*2+1);
            const station = this.addStation(worldLocation.x, worldLocation.y, "stn_" + i.toString(), 0); // Neutral station initially
            
            // Add schema to room state
            this.room.state.stations.set(station.schema.id, station.schema);
        }
        
        // Assign one random station to each team
        if (this.stations.length >= 2) {
            // Get two random indexes for team stations
            const indices = Phaser.Utils.Array.NumberArray(0, this.stations.length - 1);
            Phaser.Utils.Array.Shuffle(indices);
            
            // Assign team 1 station
            const team1StationIndex = indices[0];
            const station1 = this.stations[team1StationIndex] as ServerStation;
            station1.capture(1); // Use capture method
            
            // Assign team 2 station
            const team2StationIndex = indices[1];
            const station2 = this.stations[team2StationIndex] as ServerStation;
            station2.capture(2); // Use capture method
            
            console.log(`Assigned team stations: Team 1 at index ${team1StationIndex}, Team 2 at index ${team2StationIndex}`);
        }
    }
    
    // Add pillboxes to the map
    if (mapData.pillboxes) {
        const pillboxLocations = mapData.pillboxes;
        
        this.addPillbox = (x, y, team) => {
            const pillbox = new ServerPillbox(this, x, y, team);
            this.pillboxes.push(pillbox);
            return pillbox;
        };
        
        for (let i = 0; i < pillboxLocations.length; i++) {
            const location = pillboxLocations[i];
            const worldLocation = this.gameMap.groundLayer.tileToWorldXY(location[0]*2+1, location[1]*2+1);
            const pillbox = this.addPillbox(worldLocation.x, worldLocation.y, 0) as ServerPillbox; // Neutral pillbox initially
            
            // Set ID in schema
            pillbox.schema.id = `pillbox_${i}`;
            
            // Add schema to room state
            this.room.state.pillboxes.set(pillbox.schema.id, pillbox.schema);
        }
    }
    
    // Additional setup specific to server could go here
  }
  
  // Get a spawn location for a team
  getSpawnPositionForTeam(team: number): { x: number, y: number } {
    // Try to find a station for this team
    const teamStations = this.stations.filter(station => station.team === team);
    
    // Choose a random station from the team's stations
    const station = teamStations[Math.floor(Math.random() * teamStations.length)];
    return { x: station.x, y: station.y };
  }

  // Add a player to the server game scene
  addPlayer(sessionId: string, x: number, y: number, playerName: string = "Player"): ServerTank {
    console.log(`Server adding player ${sessionId} (${playerName}) at (${x}, ${y})`);
    const tank = new ServerTank(this, x, y, sessionId);
    tank.name = playerName;
    this.players.set(sessionId, tank);
    return tank;
  }

  // Remove a player from the server game scene
  removePlayer(sessionId: string): void {
    const tank = this.players.get(sessionId);
    if (tank) {
      console.log(`Server removing player ${sessionId}`);
      tank.destroy();
      this.players.delete(sessionId);
    }
  }

  handlePlayerInput(sessionId: string, input: InputData) {
    const tank = this.players.get(sessionId);
    if (tank) {
      tank.updateInput(input);
    }
  }

  // Validate a tile-building/harvesting request and queue it on the tank.
  // Responds to the requesting client via room.sendToClient("tileBuildStarted", ...).
  handleBuildTile(sessionId: string, data: { tiles: { x: number, y: number }[], tileType: string }) {
    const tank = this.players.get(sessionId);
    if (!tank) return;

    // Validate tiles for building (ensure they are valid terrain types)
    const validTiles = [];
    let isHarvesting = false;
    let woodCost = 0;

    for (const tile of data.tiles) {
      // Get the current tile and check if it can be built on
      const currentTile = this.gameMap.getTileAt(tile.x, tile.y);
      if (!currentTile) continue;

      const tileWorldPos = this.gameMap.groundLayer.tileToWorldXY(tile.x, tile.y);
      const distance = Phaser.Math.Distance.Between(
        tank.x, tank.y,
        tileWorldPos.x, tileWorldPos.y
      );
      if (distance > PHYSICS.BUILD_MAX_DISTANCE) continue;

      const baseTileType = this.gameMap.getBaseTileType(currentTile);

      // Check if this is a forest tile (for harvesting wood)
      if (data.tileType === "forest") {
        if (baseTileType === TILE_INDICES.FOREST) {
          validTiles.push(tile);
          isHarvesting = true;
        }
      }
      // For roads and walls, check if tile is valid for building and not water
      else if (baseTileType !== TILE_INDICES.WALL) {
        validTiles.push(tile);

        // Count wood cost for roads and walls
        if (data.tileType === "road") {
          woodCost += PHYSICS.WOOD_COST_ROAD;
        } else if (data.tileType === "wall") {
          woodCost += PHYSICS.WOOD_COST_WALL;
        }
      }
    }

    // If no valid tiles, send failure response
    if (validTiles.length === 0) {
      this.room.sendToClient(sessionId, "tileBuildStarted", {
        success: false,
        reason: `No valid tiles for ${data.tileType} building.`
      });
      return;
    }

    // If building roads or walls, check wood resource
    if (!isHarvesting && woodCost > 0) {
      if (tank.schema.wood < woodCost) {
        this.room.sendToClient(sessionId, "tileBuildStarted", {
          success: false,
          reason: `Not enough wood. Need ${woodCost}, have ${tank.schema.wood}.`
        });
        return;
      }

      // Deduct wood for building
      tank.useWood(woodCost);
    }

    console.log(`Starting tile construction for ${sessionId} - ${data.tileType} at ${validTiles[0]}`);

    // Add to player's build queue
    tank.buildQueue = validTiles.map(tile => ({
      tile,
      progress: 0,
      buildTime: PHYSICS.BUILD_TIME_PER_TILE,
      playerId: sessionId,
      tileType: data.tileType,
      isHarvesting: isHarvesting
    }));

    // Send success response with the valid tiles
    this.room.sendToClient(sessionId, "tileBuildStarted", {
      success: true,
      tiles: validTiles,
      tileType: data.tileType,
      isHarvesting: isHarvesting,
      woodCost: woodCost
    });
  }

  // Validate a pillbox-placement request and place it if the tank holds one.
  // Responds to the requesting client via room.sendToClient("pillboxPlaced", ...).
  handlePlacePillbox(sessionId: string, data: { x: number, y: number, tileX: number, tileY: number }) {
    const tank = this.players.get(sessionId);
    if (!tank) return;

    // First validate that the 2x2 area is valid for pillbox placement
    const isValid = (this.gameMap as any).isPillboxPlacementValid(data.tileX, data.tileY);

    if (!isValid) {
      this.room.sendToClient(sessionId, "pillboxPlaced", {
        success: false,
        x: data.x,
        y: data.y,
        reason: "Invalid placement area."
      });
      return;
    }

    // Calculate the world position from tile coordinates
    const worldPos = this.gameMap.groundLayer.tileToWorldXY(data.tileX, data.tileY);
    const pillboxX = worldPos.x + PHYSICS.TILE_SIZE; // Center of 2x2 area
    const pillboxY = worldPos.y + PHYSICS.TILE_SIZE;

    const success = tank.placePillbox(pillboxX, pillboxY);

    // Inform client of success/failure
    this.room.sendToClient(sessionId, "pillboxPlaced", {
      success,
      x: data.x,
      y: data.y,
      reason: success ? "Pillbox placed successfully" : "Failed to place pillbox"
    });

    // If successful, send newswire message
    if (success) {
      const teamName = tank.team === 1 ? "Blue" : "Red";
      this.room.broadcastNewswire({
        type: 'pillbox_placed',
        playerId: sessionId,
        team: tank.team,
        position: { x: pillboxX, y: pillboxY },
        message: `${tank.name} from Team ${teamName} placed a pillbox.`
      });
    }
  }
  
  /**
   * Sends a newswire message to all clients
   */
  sendNewswire(message: NewswireMessage) {
    if (this.room) {
      this.room.broadcastNewswire(message);
    }
  }

  // Create a server bullet (from tank or pillbox)
  createBullet(x: number, y: number, angle: number, ownerId: string = "") {
    // Get owner information
    let ownerName = "Unknown";
    let team = 0;
    
    // Check if owner is a tank
    if (ownerId) {
      const tank = this.players.get(ownerId);
      if (tank) {
        ownerName = tank.name;
        team = tank.team;
      } else {
        // Check if owner is a pillbox
        const pillboxes = this.pillboxes.filter(p => p.schema?.id === ownerId);
        if (pillboxes.length > 0) {
          const pillbox = pillboxes[0] as ServerPillbox;
          ownerName = `Pillbox ${pillbox.schema.id.split('_').pop()}`;
          team = pillbox.team;
        }
      }
    }
    
    const bullet = new ServerBullet(this, x, y, angle, ownerId, ownerName, team);
    this.serverBullets.push(bullet);
    
    // Add to room state
    this.room.state.bullets.set(bullet.schema.id, bullet.schema);
    
    return bullet;
  }
  
  // Override the base Bullet creation
  createBulletAt(x: number, y: number, angle: number, ownerId: string = "") {
    return this.createBullet(x, y, angle, ownerId);
  }


  addStation(x: number, y: number, id: string, team: number = 0): ServerStation {
    const station = new ServerStation(this, x, y, id, team);
    this.stations.push(station);
    
    // Add to team stations list
    if (!this.teamStations[team]) {
      this.teamStations[team] = [];
    }
    this.teamStations[team].push(station);
    
    return station;
  }
  
  getRandomStationForTeam(team: number): ServerStation | null {
    const teamStations = this.teamStations[team];
    if (!teamStations || teamStations.length === 0) {
      return null;
    }
    
    const randomIndex = Phaser.Math.Between(0, teamStations.length - 1);
    return teamStations[randomIndex];
  }
  
  // Create a new pillbox at the specified location
  createPillbox(x: number, y: number, team: number, state: string = "pickup"): ServerPillbox {
    // Generate a unique ID for the pillbox
    const id = `pillbox_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    
    // Create the pillbox
    const pillbox = new ServerPillbox(this, x, y, team, state);
    this.pillboxes.push(pillbox);
    
    // Set its ID and update schema
    pillbox.schema.id = id;
    
    pillbox.updateSchema();
    
    // Add to room state
    this.room.state.pillboxes.set(pillbox.schema.id, pillbox.schema);
    
    return pillbox;
  }

  sendSnapshots(): void {
    this.worldState.players = [];
    this.players.forEach(tank => {
      let thisPlayerState = tank.schema;
      thisPlayerState.id = tank.sessionId;
      this.worldState.players.push(thisPlayerState);
    });

    const snapshot = this.room.SI.snapshot.create(this.worldState);

    this.room.broadcast("snapshot", snapshot);
  }
  
  // Game win state tracking
  private winState = {
    gameOver: false,
    winningTeam: 0,
    restartTimer: null
  };
  
  // Check if all stations are owned by one team
  checkWinCondition(): void {
    // Skip if game is already in win state
    if (this.winState.gameOver) return;
    
    // Skip if there are no stations
    if (this.stations.length === 0) return;
    
    // Get the team of the first station
    const firstStation = this.stations[0];
    const firstTeam = firstStation.team;
    
    // Skip checking neutral stations (team 0)
    if (firstTeam === 0) return;
    
    // Check if all stations belong to the same team
    const allSameTeam = this.stations.every(station => station.team === firstTeam);
    
    if (allSameTeam) {
      // All stations owned by the same team - game win!
      this.winState.gameOver = true;
      this.winState.winningTeam = firstTeam;
      
      // Send game win message
      const teamName = firstTeam === 1 ? "Blue" : "Red";
      this.sendNewswire({
        type: 'game_win',
        team: firstTeam,
        message: `Team ${teamName} has won the game by capturing all stations!`
      });
      
      // Send restart message
      this.sendNewswire({
        type: 'info',
        message: `Server will restart in 15 seconds...`
      });
      
      // Schedule server restart
      this.winState.restartTimer = setTimeout(() => {
        this.restartServer();
      }, 15000);
    }
  }
  
  // Restart the server
  restartServer(): void {
    console.log("Restarting server due to game win condition");
    
    // Send final message
    this.sendNewswire({
      type: 'info',
      message: `Server restarting now...`
    });
    
    // Reset game state and restart
    this.scene.restart();
    
    // Clear room state
    this.room.state.stations.clear();
    this.room.state.pillboxes.clear();
    this.room.state.bullets.clear();
    
    // Reset win state
    this.winState.gameOver = false;
    this.winState.winningTeam = 0;
    if (this.winState.restartTimer) {
      clearTimeout(this.winState.restartTimer);
      this.winState.restartTimer = null;
    }
  }
  
  update(time: number, delta: number): void {
    super.update(time, delta);
    
    // Update schemas for all players
    this.players.forEach(tank => {
      tank.updateSchema();
      
      // Update build queues if present
      if (tank.buildQueue && tank.buildQueue.length > 0) {
        tank.updateBuildQueue(delta);
      }
      
      // Check for collisions with pickup state pillboxes
      this.pillboxes.forEach(p => {
        if (!(p instanceof ServerPillbox)) return;
        
        const pillbox = p as ServerPillbox;
        if (pillbox.schema.state === "pickup") {
          // Calculate distance between tank and pillbox
          const distance = Phaser.Math.Distance.Between(
            tank.x, tank.y, 
            pillbox.x, pillbox.y
          );
          
          // If close enough, collect the pillbox
          if (distance < PHYSICS.PILLBOX_HITBOX_RADIUS) {
            tank.pickupPillbox(pillbox);
          }
        }
      });
    });
    
    // Update schemas for all ServerPillbox instances
    this.pillboxes.forEach(pillbox => {
      if (pillbox instanceof ServerPillbox) {
        pillbox.updateSchema();
      }
    });
    
    // Update schemas for all ServerStation instances
    this.stations.forEach(station => {
      if (station instanceof ServerStation) {
        station.updateSchema();
      }
    });
    
    // Check for win condition - all stations owned by one team
    this.checkWinCondition();
    
    // Update bullets and clean up destroyed ones
    for (let i = this.serverBullets.length - 1; i >= 0; i--) {
      const bullet = this.serverBullets[i];
      
      // Update schema position for active bullets
      if (bullet.active) {
        bullet.updateSchema();
      } else {
        // Remove from server bullets array and room state
        this.serverBullets.splice(i, 1);
        this.room.state.bullets.delete(bullet.schema.id);
      }
    }

    this.sendSnapshots();
  }
}